// context/LibellesMenuContext.tsx
//
// Surcharges de libellés de menus propres à l'entité de l'utilisateur (avec
// héritage de l'entité parente, résolu côté serveur). Chargé à la connexion et
// à chaque changement d'entité / de périmètre. Un cache localStorage par
// entité évite le « clignotement » des libellés par défaut au rechargement.
//
// Usage : const { libelle } = useLibellesMenu(); libelle('route:/rh/conges', 'Congés')
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { LibelleMenu, menuLibellesService } from '../services/menuLibelles.service';

interface LibellesMenuContextType {
  surcharges: LibelleMenu[];
  charge: boolean;
  /** Libellé affiché : surcharge (langue courante, puis toutes langues) sinon `defaut`. */
  libelle: (cle: string, defaut: string) => string;
  /** Surcharge brute retenue pour une clé (langue courante puis toutes langues). */
  surcharge: (cle: string) => LibelleMenu | undefined;
  recharger: () => Promise<void>;
}

const repli: LibellesMenuContextType = {
  surcharges: [],
  charge: false,
  libelle: (_c, d) => d,
  surcharge: () => undefined,
  recharger: async () => {},
};

const LibellesMenuContext = createContext<LibellesMenuContextType>(repli);

const cleCache = (entiteId: string) => `menus_libelles_${entiteId}`;

export const LibellesMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const entiteId = (user as any)?.scopeEntiteId || user?.entiteId || '';
  const [surcharges, setSurcharges] = useState<LibelleMenu[]>([]);
  const [charge, setCharge] = useState(false);

  const recharger = useCallback(async () => {
    if (!entiteId) { setSurcharges([]); return; }
    try {
      const r = await menuLibellesService.lister();
      const liste = Array.isArray(r?.libelles) ? r.libelles : [];
      setSurcharges(liste);
      try { localStorage.setItem(cleCache(entiteId), JSON.stringify(liste)); } catch { /* quota */ }
    } catch {
      /* API indisponible : on garde le cache / les libellés par défaut */
    } finally {
      setCharge(true);
    }
  }, [entiteId]);

  useEffect(() => {
    if (!entiteId) { setSurcharges([]); setCharge(false); return; }
    try {
      const brut = localStorage.getItem(cleCache(entiteId));
      setSurcharges(brut ? JSON.parse(brut) : []);
    } catch { setSurcharges([]); }
    recharger();
  }, [entiteId, recharger]);

  const index = useMemo(() => {
    const m = new Map<string, LibelleMenu>();
    for (const s of surcharges) m.set(`${s.cle}|${(s.langue || '').toLowerCase()}`, s);
    return m;
  }, [surcharges]);

  const langue = (i18n.language || 'fr').slice(0, 2).toLowerCase();

  const surcharge = useCallback(
    (cle: string) => index.get(`${cle}|${langue}`) || index.get(`${cle}|`),
    [index, langue],
  );

  const libelle = useCallback(
    (cle: string, defaut: string) => surcharge(cle)?.libelle || defaut,
    [surcharge],
  );

  const valeur = useMemo(
    () => ({ surcharges, charge, libelle, surcharge, recharger }),
    [surcharges, charge, libelle, surcharge, recharger],
  );

  return <LibellesMenuContext.Provider value={valeur}>{children}</LibellesMenuContext.Provider>;
};

export const useLibellesMenu = () => useContext(LibellesMenuContext);
